import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Table,
  Button,
  Modal,
  Input,
  Select,
  message,
  Popconfirm,
  Card,
  Space,
  Tag,
  Avatar,
  Typography,
  Row,
  Col,
  Divider,
  Tooltip
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import ownerService from '@/services/owner.service';
import InspectionBtn from '@/components/InspectionBtn';
import { useDispatch } from 'react-redux';
import { setSubscription } from '@/stores/slice/authSlice';
import toast from 'react-hot-toast';

const { Title, Text } = Typography;
const { Option } = Select;

const OwnerUserManagement = ({ sharedContext, subscription }) => {
  const { ships, users, loading, loadUsers, loadDashboardData } = sharedContext;
  const dispatch = useDispatch()
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      username: '',
      email: '',
      password: '',
      designation: '',
      ship_assignments: []
    }
  });

  // Handle Create User
  const handleCreateUser = () => {
    setEditingUser(null);
    reset({
      username: '',
      email: '',
      password: '',
      designation: '',
      ship_assignments: []
    });
    setModalVisible(true);
  };

  // Handle Edit User
  const handleEditUser = async (user) => {
    setEditingUser(user);
    reset({
      username: user.username,
      email: user.email,
      designation: user.designation,
      ship_assignments: user.assignedShips?.map(ship => ship.imo_number) || []
    });
    setModalVisible(true);
  };

  // Handle Form Submit
  const onSubmit = async (data) => {
    setSubmitLoading(true);
    let subscriptionpayload = subscription
    try {
      if (editingUser) {
        const response = await ownerService.owneruserupdate(data);
        toast.success('User updated successfully');
        await loadUsers();
      } else {
        const response = await ownerService.addowneruser(data);
        subscriptionpayload = { ...subscriptionpayload, max_users: subscriptionpayload.max_users - 1 };

        if (response.success) {
          dispatch(setSubscription(subscriptionpayload))
          toast.success('User created successfully');
          await loadUsers();
        }
      }

      setModalVisible(false);
      reset();
    } catch (error) {
      toast.error(error.message || 'Operation failed');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle Delete User
  const handleDeleteUser = async (userId) => {
    try {
      // Add your delete API call here
      // const response = await ownerService.deleteUser(userId);
      toast.success('User deleted successfully');
      await loadUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  // Filter users based on search text
  const filteredUsers = users.filter(user =>
    user.username?.toLowerCase().includes(searchText.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchText.toLowerCase()) ||
    user.designation?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'User',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <div className="flex items-center space-x-3">
          <Avatar size={40} icon={<UserOutlined />} className="bg-blue-500" />
          <div className="ms-4">
            <div className="font-semibold text-gray-800">{text}</div>
            <div className="text-sm text-gray-500 flex items-center">
              <MailOutlined className="mr-1" />
              {record.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Designation & Role',
      key: 'designation',
      render: (_, record) => (
        <div>
          <Tag color="blue" className="mt-1">
            {record.designation}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Assigned Ships',
      key: 'shipAssignments',
      render: (_, record) => (
        <div className="max-w-xs">
          {record?.shipAssignments?.length > 0 ? (
            record?.shipAssignments.map(ships => (
              <Tag key={ships.ship.imo_number} color="green" className="mb-1 mr-1">
                {ships.ship.name}
              </Tag>
            ))
          ) : (
            <Text type="secondary">No ships assigned</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit User">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={() => handleDeleteUser(record.user_id)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Tooltip title="Delete User">
              <Button
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-2 mb-3">
        <Row justify="space-between" align="middle"  >
          <Col className='flex'>
            <Title level={5} className="mb-0 text-gray-800">
              <UserOutlined className="mr-3 text-blue-600" />
              User Management ({filteredUsers?.length})
            </Title>
          </Col>
          <Col>
            <Space>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Search users by username, email, or designation"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="middle"
              />
              <InspectionBtn
                type="primary"
                size='small'
                // disabled={subscription?.max_users >= 0 ? true : false}
                icon={<PlusOutlined />}
                onClick={handleCreateUser}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Add New User
              </InspectionBtn>
            </Space>
          </Col>
        </Row>

      </div>

      {/* Users Table */}
      <div className="shadow-sm p-2">
        <Table
          size="small"
          columns={columns}
          dataSource={filteredUsers}
          rowKey="user_id"
          className="spotflash-table"
          loading={loading}
          scroll={{ x: 'max-content' }} // 👈 adds horizontal scroll on small screens

          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
          }}
          rowClassName={(record, index) =>
            index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
          }
        />
      </div>

      {/* Create/Edit User Modal */}
      <Modal
        title={
          <div className="flex items-center text-lg">
            <UserOutlined className="mr-2 text-blue-600" />
            {editingUser ? 'Edit User' : 'Create New User'}
          </div>
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          reset();
        }}
        footer={null}
        width={700}
        className="top-10"
      >
        <Divider />
        <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
          <Row gutter={16}>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username *
                </label>
                <Controller
                  name="username"
                  control={control}
                  rules={{
                    required: 'Please enter username',
                    minLength: {
                      value: 3,
                      message: 'Username must be at least 3 characters'
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      prefix={<UserOutlined />}
                      placeholder="Enter username"
                      size="large"
                      status={errors.username ? 'error' : ''}
                    />
                  )}
                />
                {errors.username && (
                  <span className="text-red-500 text-sm">{errors.username.message}</span>
                )}
              </div>
            </Col>
            <Col span={12}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'Please enter email',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter valid email'
                    }
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      prefix={<MailOutlined />}
                      placeholder="Enter email"
                      size="large"
                      status={errors.email ? 'error' : ''}
                    />
                  )}
                />
                {errors.email && (
                  <span className="text-red-500 text-sm">{errors.email.message}</span>
                )}
              </div>
            </Col>
          </Row>

          {!editingUser && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: 'Please enter password',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters'
                  }
                }}
                render={({ field }) => (
                  <Input.Password
                    {...field}
                    placeholder="Enter password"
                    size="large"
                    status={errors.password ? 'error' : ''}
                  />
                )}
              />
              {errors.password && (
                <span className="text-red-500 text-sm">{errors.password.message}</span>
              )}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Designation *
            </label>
            <Controller
              name="designation"
              control={control}
              rules={{ required: 'Please enter designation' }}
              render={({ field }) => (
                <Input
                  {...field}
                  prefix={<IdcardOutlined />}
                  placeholder="Enter designation"
                  size="large"
                  status={errors.designation ? 'error' : ''}
                />
              )}
            />
            {errors.designation && (
              <span className="text-red-500 text-sm">{errors.designation.message}</span>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ship Assignments *
            </label>
            <Controller
              name="ship_assignments"
              control={control}
              rules={{ required: 'Please select at least one ship' }}
              render={({ field }) => (
                <Select
                  mode="multiple"
                  {...field}
                  placeholder="Select ships to assign"
                  size="large"
                  style={{ width: '100%' }}
                  status={errors.ship_assignments ? 'error' : ''}
                  optionLabelProp="label"
                  maxTagCount="responsive"
                >
                  {ships.map(ship => (
                    <Option
                      key={ship.imo_number}
                      value={ship.imo_number}
                      label={`${ship.name} (${ship.vessel_type || 'Unknown Type'})`}
                    >
                      <div>
                        <div className="font-medium">{ship.name}</div>
                        <div className="text-sm text-gray-500">
                          {ship.imo_number} • {ship.vessel_type || 'Unknown Type'}
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.ship_assignments && (
              <span className="text-red-500 text-sm">{errors.ship_assignments.message}</span>
            )}
            <div className="text-xs text-gray-500 mt-1">
              {ships.length} ships available for assignment
            </div>
          </div>

          <Divider />
          <div className="flex justify-end space-x-2">
            <InspectionBtn variant='secondary'
              size="large"
              onClick={() => {
                setModalVisible(false);
                reset();
              }}
            >
              Cancel
            </InspectionBtn>
            <InspectionBtn
              type="primary"
              size="large"
              htmlType="submit"
              loading={submitLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editingUser ? 'Update User' : 'Create User'}
            </InspectionBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default OwnerUserManagement;